
import {
  maxNumberRules,
  notRequiredNumberRules,
  numberRules,
  requiredRules,
} from '@/features/common/helpers/validationRules';
import {
  dimUnitsItems,
  DimUnitsOptionType,
  FreightLimit,
  PackageOptionsType,
  packageTypes,
  weightUnitsItems,
  WeightUnitsOptionType,
} from '@/features/common/raw';
import { UserPreferences } from '@/features/settings/types';
import {
  Component, PropSync, Prop, Mixins, Watch, Inject,
} from 'vue-property-decorator';
import { InputValidationRules } from 'vuetify';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import {
  rulesIfAttempted as rulesIfAttemptedFn,
  CreateShipmentValidationState,
} from '@/features/shipment/helpers/createShipmentValidation';
import { trackCreateFieldComplete } from '@/features/common/helpers/tracked-events';
import {
  InProgress,
  PackageItem,
  PackageType,
} from '../../types';

export interface FormData extends Pick<
  PackageItem,
  'packageType' |
  'weightUnits' |
  'dimUnits' |
  'isAdditionalHandling' |
  'description'
> {
  weight: number | '',
  length: number | '',
  height: number | '',
  width: number | '',
  insurance: number | '',
}

export interface FormLabel {
  length: string,
  width: string,
  height: string,
  declaredValue: string,
}

@Component
export default class AddPackageInputs extends Mixins(ResponsivenessMixin) {
  @Inject({
    from: 'createShipmentValidationState',
    default: () => ({ attempted: true }),
  })
  private createShipmentValidationState!: CreateShipmentValidationState;

  @PropSync('packageData', { required: true }) packageDataSync!: FormData;

  @PropSync('isFormChanged', { required: true }) isFormChangedSync!: boolean;

  @Prop({ required: true }) packageSummary!: PackageItem[];

  // this flag will determine whether to create shipment or just estimate
  @Prop({ required: false, default: false }) isJustEstimate!: boolean;

  @Prop({ required: false, default: true }) isResetOnPackageChange!: boolean;

  private maxNumberLength = 50000;

  private rulesIfAttempted(rules?: InputValidationRules): InputValidationRules {
    return rulesIfAttemptedFn(rules, this.createShipmentValidationState.attempted);
  }

  private requiredRules = requiredRules;

  private numberRules = numberRules;

  private maxNumberRules = maxNumberRules;

  private notRequiredNumberRules = notRequiredNumberRules;

  private weightUnitsItems: WeightUnitsOptionType = weightUnitsItems;

  private dimUnitsItems: DimUnitsOptionType = dimUnitsItems;

  private dimensionsRequired = true;

  get isUserPreferences(): number {
    return this.$store.getters.getIsUserPreferences;
  }

  get userPreferences(): UserPreferences {
    return this.$store.getters.getUserPreferences;
  }

  get inProgressData(): InProgress {
    return this.$store.getters.getInProgress;
  }

  get isFreightPackage(): boolean {
    const { packageType, weight, weightUnits } = this.packageDataSync;

    if (packageType === 'Pallet') return true;
    if (weightUnits === 'Lbs' && weight > FreightLimit.Lbs) return true;
    if (weightUnits === 'Kgs' && weight > FreightLimit.Kgs) return true;

    return false;
  }

  get dimensionsRules(): InputValidationRules {
    return this.dimensionsRequired || this.isFreightPackage
      ? [...this.requiredRules, ...this.numberRules]
      : [...this.notRequiredNumberRules];
  }

  get dimensionsUnitRules(): InputValidationRules {
    if (
      !this.packageDataSync.length
          && !this.packageDataSync.width
          && !this.packageDataSync.height
    ) return [];

    return this.requiredRules;
  }

  get packageTypes(): PackageOptionsType {
    if (!this.packageSummary.length) return packageTypes;

    const isPallet = !!this.packageSummary.find((p) => p.packageType === 'Pallet');

    if (isPallet) return packageTypes.filter((p) => p.value === 'Pallet');

    return packageTypes.filter((p) => p.value !== 'Pallet');
  }

  get disableAll(): boolean {
    return this.packageDataSync.packageType === 'Envelope';
  }

  get disableDimensions(): boolean {
    return this.packageDataSync.packageType === 'Pak'
      || this.packageDataSync.packageType === 'Envelope';
  }

  get formLabels(): FormLabel {
    const dimRequired = this.dimensionsRequired || this.isFreightPackage;
    const star = dimRequired ? ' *' : '';
    const labels = {
      length: (this.$t('labels.length') as string) + star,
      width: (this.$t('labels.width') as string) + star,
      height: (this.$t('labels.height') as string) + star,
      declaredValue: this.$t('labels.declaredValue') as string,
    };

    if ((this.screenWidth < 920 && this.screenWidth >= 768) || this.screenWidth < 600) {
      labels.length = (this.$t('labels.abbrs.length') as string) + star;
      labels.width = (this.$t('labels.abbrs.width') as string) + star;
      labels.height = (this.$t('labels.abbrs.height') as string) + star;
      labels.declaredValue = this.$t('labels.abbrs.declaredValue') as string;
    }

    return labels;
  }

  get isShowAdditionalHandling(): boolean {
    return this.packageDataSync.packageType !== 'Pak';
  }

  get declaredValueCurrency(): 'CAD' | 'USD' {
    const getCountryCode = (countryCode?: string): string => {
      return countryCode || this.userPreferences?.shippingSettings?.defaultAddress?.countryCode || '';
    };

    if (this.isJustEstimate) {
      const { estimateData } = this.$store.state.estimate;
      if (!estimateData || !estimateData.fromAddress) {
        const countryCode = this.userPreferences?.shippingSettings?.defaultAddress?.countryCode;
        return countryCode === 'CA' ? 'CAD' : 'USD';
      }
      const countryCode = getCountryCode(estimateData.fromAddress.countryCode);
      return countryCode === 'CA' ? 'CAD' : 'USD';
    }

    if (!this.inProgressData || !this.inProgressData.fromAddress) {
      const countryCode = this.userPreferences?.shippingSettings?.defaultAddress?.countryCode;
      return countryCode === 'CA' ? 'CAD' : 'USD';
    }

    const countryCode = getCountryCode(this.inProgressData.fromAddress.countryCode);
    return countryCode === 'CA' ? 'CAD' : 'USD';
  }

  @Watch('isUserPreferences', { immediate: true })
  private setUserPrefsData(newValue: number): void {
    if (!newValue) return;

    this.dimensionsRequired = this.userPreferences.shippingSettings.dimensionsMandatory;
  }

  private packageTypeChangeHandler(newValue: keyof typeof PackageType): void {
    if (!this.isResetOnPackageChange) return;

    const clearDims = () => {
      this.packageDataSync.length = '';
      this.packageDataSync.height = '';
      this.packageDataSync.width = '';
    };

    if (newValue === 'Envelope') {
      this.packageDataSync.weight = 1;
      this.packageDataSync.weightUnits = 'Lbs';
      this.packageDataSync.insurance = '';
      this.packageDataSync.isAdditionalHandling = false;
    } else if (newValue === 'MyPackage' || newValue === 'Pak') {
      this.packageDataSync.weight = '';
      this.packageDataSync.weightUnits = this.userPreferences.shippingSettings.weightUnits;
    }

    clearDims();

    this.updateData(true);
  }

  private updateField(fieldName: 'weight' | 'length' | 'width' | 'height' | 'insurance') {
    let value = parseFloat(String(this.packageDataSync[fieldName]));

    if (Number.isNaN(value)) return;

    value = Math.round(value * 10) / 10;
    this.packageDataSync[fieldName] = value;

    if (value <= this.maxNumberLength) return;

    this.packageDataSync[fieldName] = this.maxNumberLength;
  }

  private updateData(
    force = false,
    sync = true,
    fieldName?: 'weight' | 'length' | 'width' | 'height' | 'insurance',
  ) {
    if (fieldName) this.updateField(fieldName);

    if (fieldName === 'weight' && this.packageDataSync.weight) {
      trackCreateFieldComplete(this.$gtag, { field: 'weight' });
    }
    if (fieldName && ['length', 'width', 'height'].includes(fieldName)
      && this.packageDataSync.length && this.packageDataSync.width && this.packageDataSync.height) {
      trackCreateFieldComplete(this.$gtag, { field: 'dimensions' });
    }

    this.$emit('update', force, sync);
  }
}
